import type {
  BindCardAuditCondition,
  BindCardAuditItem,
  BindCardAuditRawItem,
  BindCardLimitTransferParams,
  BindCardLimitTransferResponse,
  BindCardTransferActionParams,
  BindCardTransferActionResponse,
  BindCardTransferQuery,
  BindCardTransferRawItem,
  BindCardTransferRecord,
  BindCardTransferTimeline,
  BindCardTransferTimelineQuery,
  BindCardVerifyActionParams,
  BindCardVerifyActionResponse,
  BindCardVerifyDetail,
  BindCardVerifyDetailQuery,
  BindCardVerifyRetryParams,
  BindCardVerifySubmitParams,
  BindCardVerifySubmitResponse,
  BindCondition,
  BindItemRaw,
  CardCondition,
  CardOpenDefaultLimitResponse,
  CardSensitiveConfirmParams,
  CardSensitiveConfirmResponse,
  CardSensitiveInitParams,
  CardSensitiveInitResponse,
  CheckCondition,
  CheckItemRaw,
  FreezeCardParams,
  SaveBindParams,
  SubmitCardApplyParams,
  SwitchActiveCardParams,
  SyncCardLimitsResponse,
  UnbindCardParams,
  UnbindCardResponse,
} from './types'

import type { PagingRequest, PagingResponse } from '#/api/type'

import { defineApi } from '#/api/define-api'
import { requestClient } from '#/api/request'
import { DateRanges } from '#/utils'

export const bindCardAccountApi = defineApi('operation', ({ prefix }) => {
  const DEFAULT_CARD_CURRENCY = 'USD'

  function getBindList(params: PagingRequest<BindCondition>) {
    return requestClient
      .post(prefix('cardBind/query'), {
        condition: {
          accountId: params.condition.accountId,
          accountName: params.condition.accountName,
          cardId: params.condition.cardId,
          cardStatus: params.condition.cardStatus,
          customerId: Number(params.condition?.customerId),
          customerName: params.condition.customerName,
          isActiveCard: params.condition.isActiveCard,
          last4: params.condition.last4,
          mediaPlatforms: params.condition.mediaPlatforms,
          verifyStatus: params.condition.verifyStatus,
        },
        pageIndex: params.pageIndex,
        pageSize: params.pageSize,
      })
      .then((res) => ({
        list: (res.list || []).map((item: BindItemRaw) => {
          const cards = (item.cards || []).map((card) => ({
            availableCreditLimit: card.cardAvailableLimit,
            bindId: card.bindId,
            canApplyVerify: card.canApplyVerify,
            canCancelVerify: card.canCancelVerify,
            canFreeze: card.canFreeze,
            canMarkVerified: card.canMarkVerified,
            canReclaimVerify: card.canReclaimVerify,
            canTransferOut: card.canTransferOut,
            canUnbind: card.canUnbind,
            cardCurrency: card.cardCurrency,
            cardId: card.cardId,
            cardName: card.cardName,
            cardStatus: card.cardStatus,
            createdTime: card.cardCreatedAt,
            isActiveCard: card.isActiveCard,
            last4: card.last4,
            rowKey: `card-${card.cardId}`,
            totalCreditLimit: card.cardTotalLimit,
            usedCreditLimit: card.cardUsedLimit,
            verifyApplyAmount: card.verifyApplyAmount,
            verifyDisplayStatus: card.verifyDisplayStatus,
            verifyStatus: card.verifyStatus,
            verifyTaskNo: card.verifyTaskNo,
          }))
          const pendingCards = (item.pendingApplies || []).map((apply) => ({
            applyId: apply.applyId,
            applyNo: apply.applyNo,
            cardCurrency: DEFAULT_CARD_CURRENCY,
            cardStatus: apply.status,
            createdTime: apply.createdTime,
            isPendingApply: true,
            remark: apply.remark,
            rowKey: `apply-${apply.applyId}`,
          }))

          return {
            accountId: item.accountId,
            accountName: item.accountName,
            activeCardLast4:
              item.activeCardLast4 ??
              cards.find((card) => card.isActiveCard)?.last4 ??
              '',
            activeCardVerifyHint: item.activeCardVerifyHint,
            associatedCardCount: item.cardCount,
            cardRows: [...cards, ...pendingCards],
            cards,
            customerId: item.customerId,
            customerName: item.customerName,
            lastOperator: item.lastOperator || item.updatedBy,
            mediaPlatform: item.mediaPlatform,
            pendingCards,
            usingCardAvailableCreditLimit: item.activeCardAvailableLimit,
            usingCardCurrency: item.activeCardCurrency,
            usingCardId: item.activeCardId,
            updatedBy: item.updatedBy,
            updatedTime: item.updatedTime,
          }
        }),
        total: res.total,
      }))
  }

  function getCardList(params: PagingRequest<CardCondition>) {
    return requestClient
      .post(prefix('cardBind/cards'), {
        condition: {
          cardId: params.condition.cardId,
          dateRange: DateRanges.datesOrNull(params.condition.dateRange),
          last4: params.condition.last4,
          name: params.condition.name,
          onlyActive: params.condition.onlyActive,
        },
        pageIndex: params.pageIndex,
        pageSize: params.pageSize,
      })
      .then((res) => ({
        list: (res.list || []).map((item: any) => ({
          cardId: item.cardId,
          cardName: item.cardName,
          last4: item.last4,
          isBound: item.isBound,
          status: item.status,
        })),
        total: res.total,
      }))
  }

  function saveBind(params: SaveBindParams) {
    return requestClient.post(prefix('cardBind/bind'), {
      accountId: params.accountId,
      cardId: params.cardId,
      mediaPlatform: params.mediaPlatform,
    })
  }

  function submitCardApply(params: SubmitCardApplyParams) {
    return requestClient.post(prefix('cardBind/apply'), {
      accountId: params.accountId,
      currency: params.currency,
      initialLimit: params.initialLimit,
      mediaPlatform: params.mediaPlatform,
      remark: params.remark,
    })
  }

  /**
   * 查询开卡默认初始额度与币种。
   * POST /cardBind/cards/openDefaultLimit
   */
  function getCardOpenDefaultLimit() {
    return requestClient.post<CardOpenDefaultLimitResponse>(
      prefix('cardBind/cards/openDefaultLimit'),
      {},
    )
  }

  function unbindCard(params: UnbindCardParams) {
    return requestClient.post<UnbindCardResponse>(prefix('cardBind/unbind'), {
      bindId: params.bindId,
    })
  }

  function freezeCard(params: FreezeCardParams) {
    return requestClient.post(prefix('cardBind/cards/freeze'), {
      cardId: params.cardId,
    })
  }

  function syncSlashCardLimitStatus() {
    return requestClient.post<SyncCardLimitsResponse>(
      prefix('cardBind/cards/sync'),
      {},
    )
  }

  function switchActiveCard(params: SwitchActiveCardParams) {
    return requestClient.post(prefix('cardBind/switch'), {
      accountId: params.accountId,
      mediaPlatform: params.mediaPlatform,
      targetCardId: params.targetCardId,
    })
  }

  function initCardSensitiveView(params: CardSensitiveInitParams) {
    return requestClient.post<CardSensitiveInitResponse>(
      prefix('cardBind/sensitive/init'),
      {
        cardId: params.cardId,
        redirectUrl: params.redirectUrl,
      },
    )
  }

  function confirmCardSensitiveView(params: CardSensitiveConfirmParams) {
    return requestClient.post<CardSensitiveConfirmResponse>(
      prefix('cardBind/sensitive/confirm'),
      {
        cardId: params.cardId,
        cardLast4: params.cardLast4,
        sessionId: params.sessionId,
        verifyOpenId: params.verifyOpenId,
        verifyPerson: params.verifyPerson,
      },
    )
  }

  function getAuditList(params: PagingRequest<BindCardAuditCondition>) {
    return requestClient
      .post<PagingResponse<BindCardAuditRawItem>>(prefix('cardBind/audit'), {
        condition: {
          cardId: params.condition.cardId,
          last4: params.condition.last4,
          operateTimeRange: DateRanges.datesOrNull(
            params.condition.operateTimeRange,
          ),
          operator: params.condition.operator,
          result: params.condition.result,
          verifyPerson: params.condition.verifyPerson,
        },
        orderBy: params.orderBy,
        pageIndex: params.pageIndex,
        pageSize: params.pageSize,
        sortBy: params.sortBy,
      })
      .then((res) => ({
        list: (res.list || []).map(
          (item: BindCardAuditRawItem): BindCardAuditItem => ({
            cardId: item.cardId,
            cardLast4: item.cardLast4,
            device: item.device,
            failReason: item.failReason,
            id: item.id,
            ip: item.ip,
            operateTime: item.operateTime,
            operator: item.operator,
            result: item.result,
            verifyMethod: item.verifyMethod,
            verifyPerson: item.verifyPerson,
          }),
        ),
        total: res.total,
      }))
  }

  /**
   * 查询额度转移记录列表。
   * POST /cardBind/limitTransfer/query
   */
  function getLimitTransferList(params: PagingRequest<BindCardTransferQuery>) {
    const condition = params.condition

    return requestClient
      .post<PagingResponse<BindCardTransferRawItem>>(
        prefix('cardBind/limitTransfer/query'),
        {
          condition: {
            accountId: condition.accountId,
            customerId: condition.customerId,
            customerName: condition.customerName,
            fromCardId: condition.fromCardId,
            onlyOpen: condition.onlyOpen,
            status: condition.status,
            toCardId: condition.toCardId,
            transferNo: condition.transferNo,
            updatedTimeRange: DateRanges.datesOrNull(
              condition.updatedTimeRange,
            ),
          },
          orderBy: params.orderBy,
          pageIndex: params.pageIndex,
          pageSize: params.pageSize,
          sortBy: params.sortBy,
        },
      )
      .then((res) => ({
        list: res.list.map(
          (item: BindCardTransferRawItem): BindCardTransferRecord => ({
            ...item,
          }),
        ),
        total: res.total,
      }))
  }

  /**
   * 查询额度转移履历。
   * POST /cardBind/limitTransfer/timeline
   */
  function getLimitTransferTimeline(
    params: BindCardTransferTimelineQuery,
  ): Promise<BindCardTransferTimeline> {
    return requestClient
      .post<BindCardTransferTimeline>(
        prefix('cardBind/limitTransfer/timeline'),
        {
          transferNo: params.transferNo,
        },
      )
      .then((res) => ({
        events: res.events.map((item) => ({
          createdTime: item.createdTime,
          detail: item.detail,
          eventType: item.eventType,
          fromStatus: item.fromStatus,
          fundOrderNo: item.fundOrderNo,
          operator: item.operator,
          toStatus: item.toStatus,
        })),
        status: res.status,
        transferNo: res.transferNo,
      }))
  }

  /**
   * 处理额度转移异常。
   * POST /cardBind/limitTransfer/action
   */
  function executeLimitTransferAction(
    params: BindCardTransferActionParams,
  ): Promise<BindCardTransferActionResponse> {
    return requestClient
      .post<BindCardTransferActionResponse>(
        prefix('cardBind/limitTransfer/action'),
        {
          action: params.action,
          newToCardId: params.newToCardId,
          transferNo: params.transferNo,
        },
      )
      .then((res) => ({
        status: res.status,
        transferNo: res.transferNo,
      }))
  }

  /**
   * 提交同账户额度转移。
   * POST /cardBind/limitTransfer/submit
   */
  function submitLimitTransfer(params: BindCardLimitTransferParams) {
    return requestClient.post<BindCardLimitTransferResponse>(
      prefix('cardBind/limitTransfer/submit'),
      {
        accountId: params.accountId,
        amount: params.amount,
        confirmUnclaimedWarn: params.confirmUnclaimedWarn,
        fromCardId: params.fromCardId,
        mediaPlatform: params.mediaPlatform,
        toCardId: params.toCardId,
      },
    )
  }

  /**
   * 申请验卡初始额度。
   * POST /cardBind/verify/submit
   */
  function submitCardVerify(params: BindCardVerifySubmitParams) {
    return requestClient.post<BindCardVerifySubmitResponse>(
      prefix('cardBind/verify/submit'),
      {
        applyAmount: params.applyAmount,
        attachments: params.attachments,
        cardId: params.cardId,
        remark: params.remark,
        screenshotUrls: params.screenshotUrls,
      },
    )
  }

  /**
   * 查询验卡任务详情。
   * POST /cardBind/verify/detail
   */
  function getCardVerifyDetail(params: BindCardVerifyDetailQuery) {
    return requestClient.post<BindCardVerifyDetail>(
      prefix('cardBind/verify/detail'),
      {
        cardId: params.cardId,
        taskNo: params.taskNo,
      },
    )
  }

  /**
   * 标记已验卡。
   * POST /cardBind/verify/markVerified
   */
  function markCardVerified(params: BindCardVerifyRetryParams) {
    return requestClient.post<BindCardVerifyActionResponse>(
      prefix('cardBind/verify/markVerified'),
      { taskNo: params.taskNo },
    )
  }

  /**
   * 取消验卡任务并降回快照。
   * POST /cardBind/verify/cancel
   */
  function cancelCardVerify(params: BindCardVerifyActionParams) {
    return requestClient.post<BindCardVerifyActionResponse>(
      prefix('cardBind/verify/cancel'),
      {
        remark: params.remark,
        taskNo: params.taskNo,
      },
    )
  }

  /**
   * 确认验卡退款并回收。
   * POST /cardBind/verify/reclaim
   */
  function reclaimCardVerify(params: BindCardVerifyActionParams) {
    return requestClient.post<BindCardVerifyActionResponse>(
      prefix('cardBind/verify/reclaim'),
      {
        remark: params.remark,
        slashTxnNo: params.slashTxnNo,
        taskNo: params.taskNo,
      },
    )
  }

  /**
   * 验卡额度失败重试。
   * POST /cardBind/verify/retryRaise
   */
  function retryCardVerifyRaise(params: BindCardVerifyRetryParams) {
    return requestClient.post<BindCardVerifyActionResponse>(
      prefix('cardBind/verify/retryRaise'),
      { taskNo: params.taskNo },
    )
  }

  function getCheckList(params: PagingRequest<CheckCondition>) {
    return requestClient
      .post(prefix('cardBind/reconcile'), {
        condition: {
          accountId: params.condition.accountId,
          accountName: params.condition.accountName,
          cardId: params.condition.cardId,
          customerId: Number(params.condition?.customerId),
          customerName: params.condition.customerName,
          last4: params.condition.last4,
          dateRange: DateRanges.datesOrNull(params.condition.dateRange),
          mediaPlatforms: params.condition.mediaPlatforms,
        },
        orderBy: params.orderBy,
        pageIndex: params.pageIndex,
        pageSize: params.pageSize,
        sortBy: params.sortBy,
      })
      .then((res) => ({
        list: (res.list || []).map((item: CheckItemRaw) => ({
          accountCurrency: item.accountCurrency,
          accountId: item.accountId,
          accountName: item.accountName,
          accountSpend: item.accountSpend,
          cards: item.cards || [],
          customerId: item.customerId,
          customerName: item.customerName ?? item.companyName,
          date: item.date,
          mediaPlatform: item.mediaPlatform,
        })),
        summary: {
          accountSpendTotal: res.summary?.accountSpendTotal,
          cardRebateTotal: res.summary?.cardRebateTotal,
          cardSpendTotal: res.summary?.cardSpendTotal,
        },
        total: res.total,
      }))
  }

  return {
    getAuditList,
    getBindList,
    getCardList,
    getCardOpenDefaultLimit,
    getCheckList,
    getLimitTransferList,
    getLimitTransferTimeline,
    executeLimitTransferAction,
    submitLimitTransfer,
    submitCardVerify,
    getCardVerifyDetail,
    markCardVerified,
    cancelCardVerify,
    reclaimCardVerify,
    retryCardVerifyRaise,
    initCardSensitiveView,
    saveBind,
    submitCardApply,
    confirmCardSensitiveView,
    freezeCard,
    switchActiveCard,
    syncSlashCardLimitStatus,
    unbindCard,
  }
})
